/**
 * The facility inspections' data: the inspection store, kept on the platform
 * row by row (build 5: one row per inspection, contractor, past repair and
 * work-order link, so two inspectors never overwrite each other), and the
 * work orders — the ONE
 * register the platform shares, so a work order raised here is the same record
 * the work-orders board and Operations control show, and a status changed here
 * is changed there.
 */
import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useSession } from "../../app/SessionContext";
import { useWorkOrders } from "../../api/queries";
import {
  INSPECTION_TOPIC,
  PRIORITY_FOR,
  linkWorkOrder,
  tradeOf,
  workOrderTitle,
  type Fault,
  type InspectionRecord,
  type InspectionStore,
  withWorkDone,
} from "../../domain/assetInspections";
import { FACILITY_SPEC } from "../../domain/registerSpecs";
import { FACILITY_BY_ID } from "../../domain/facilityRegister";
import type { TransitionInput, WorkOrder } from "../../domain/workOrders";
import { formatDate } from "../../lib/format";
import { useRegisterDoc } from "../shared/useRegisterRows";
import { itemText } from "./labels";

export { INSPECTION_TOPIC };

/** The store, with each finding's work order looked up so a finding whose work is done is closed. */
export function useInspectionStore() {
  const { orders } = useAllWorkOrders();
  const view = useCallback((store: InspectionStore) => withWorkDone(store, orders), [orders]);
  return useRegisterDoc(FACILITY_SPEC, { view });
}

export function useAllWorkOrders(): { orders: WorkOrder[]; loading: boolean; truncated: boolean } {
  const q = useWorkOrders();
  return { orders: q.data?.items ?? [], loading: q.isPending, truncated: !!q.data?.truncated };
}

/** Raising and moving work orders, with the register refreshed everywhere after. */
export function useWorkOrderActions() {
  const session = useSession();
  const qc = useQueryClient();
  const refresh = () => qc.invalidateQueries({ queryKey: ["work-orders"] });

  /*
   * Raised against the facility id as its asset tag. A platform that does not
   * hold the facility as an asset refuses the tag; the order is then raised
   * without it, and the "F11 · …" at the head of the title is how every screen
   * still finds it.
   */
  const raise = async (
    store: InspectionStore,
    rec: InspectionRecord,
    fault: Fault,
  ): Promise<{ store: InspectionStore; order: WorkOrder }> => {
    const facility = FACILITY_BY_ID.get(rec.facilityId)!;
    const trade = tradeOf(rec.trade);
    const what = itemText(trade, fault.itemId);
    const severity = fault.result as keyof typeof PRIORITY_FOR;
    const input = {
      siteId: session.site.id,
      title: workOrderTitle(facility, trade, what),
      kind: "corrective" as const,
      priority: PRIORITY_FOR[severity],
      // where it is, the trade, and the trade's contractor, so the work-order list sorts it
      area: facility.domain === "airfield" ? "airside" : facility.domain === "terminal" ? "terminal" : "landside",
      trade: rec.trade,
      contractor: store.contractors[rec.trade]?.company || undefined,
      description: [
        `Trade: ${trade.label}`,
        `Repair type: ${fault.repairType ?? "Not classified"}`,
        `Finding: ${severity} — ${what}`,
        fault.note ? `Note: ${fault.note}` : "",
        `Found by: ${rec.company} (${rec.inspector}), ${rec.form} inspection ${rec.id} on ${formatDate(rec.at)}`,
      ]
        .filter(Boolean)
        .join("\n"),
    };
    let order: WorkOrder;
    try {
      order = await session.source.createWorkOrder({ ...input, assetTag: facility.id });
    } catch {
      order = await session.source.createWorkOrder(input);
    }
    void refresh();
    return { store: linkWorkOrder(store, rec.id, fault.itemId, order), order };
  };

  const move = async (order: WorkOrder, input: TransitionInput): Promise<WorkOrder> => {
    const next = await session.source.transitionWorkOrder(order.id, input);
    void refresh();
    return next;
  };

  return { raise, move };
}
